import {
  InfraCostsSummarySchema,
  InfraPaymentSchema,
  InfraServiceSchema,
  type InfraCostsSummary,
  type InfraPayment,
  type InfraService,
  type InfraServiceInput,
  type InfraServicePatch,
  type RecordInfraPaymentInput,
} from '@carsfinders/contracts';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { apiFetch } from '../../lib/api';

/**
 * «Інфраструктура» data layer — apps/api `InfraCostsController` (superadmin):
 *
 *   GET   /api/admin/infra-costs/services?includeArchived=true -> InfraService[]
 *   POST  /api/admin/infra-costs/services                      -> InfraService
 *   PATCH /api/admin/infra-costs/services/:id                  -> InfraService
 *   POST  /api/admin/infra-costs/services/:id/archive          -> InfraService
 *   POST  /api/admin/infra-costs/services/:id/payments         -> { payment, service }
 *         (header `Idempotency-Key: <uuid>`; a repeat returns the same payment)
 *   GET   /api/admin/infra-costs/payments?from=&to=            -> InfraPayment[]
 *   GET   /api/admin/infra-costs/summary                       -> InfraCostsSummary
 *   POST  /api/admin/infra-costs/seed-defaults                 -> { created, existing }
 *
 * Every answer goes through its contract schema (same rule as analytics/api.ts).
 */

const ROOT = 'admin/infra-costs';

export const INFRA_COSTS_QUERY_KEY = ['admin', 'infra-costs'] as const;
export const INFRA_SERVICES_QUERY_KEY = [...INFRA_COSTS_QUERY_KEY, 'services'] as const;
export const INFRA_SUMMARY_QUERY_KEY = [...INFRA_COSTS_QUERY_KEY, 'summary'] as const;
export const INFRA_PAYMENTS_QUERY_KEY = [...INFRA_COSTS_QUERY_KEY, 'payments'] as const;

export interface RecordInfraPaymentResult {
  payment: InfraPayment;
  service: InfraService;
}

export interface InfraSeedResult {
  created: number;
  existing: number;
}

const asRecord = (payload: unknown): Record<string, unknown> =>
  payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};

function parseCount(value: unknown, field: string): number {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
    throw new Error(`seed-defaults: '${field}' is not a count`);
  }
  return value;
}

const servicePath = (id: string) => `${ROOT}/services/${encodeURIComponent(id)}`;

export async function fetchInfraServices(includeArchived: boolean): Promise<InfraService[]> {
  const payload = await apiFetch<unknown>(`${ROOT}/services`, {
    // Omitted rather than `false`: the server's default is already active-only.
    searchParams: { includeArchived: includeArchived ? 'true' : undefined },
  });
  return InfraServiceSchema.array().parse(payload);
}

export async function fetchInfraSummary(): Promise<InfraCostsSummary> {
  return InfraCostsSummarySchema.parse(await apiFetch<unknown>(`${ROOT}/summary`));
}

export async function fetchInfraPayments(range: { from?: string; to?: string }): Promise<InfraPayment[]> {
  const payload = await apiFetch<unknown>(`${ROOT}/payments`, {
    searchParams: { from: range.from, to: range.to },
  });
  return InfraPaymentSchema.array().parse(payload);
}

export async function createInfraService(input: InfraServiceInput): Promise<InfraService> {
  const payload = await apiFetch<unknown>(`${ROOT}/services`, { method: 'POST', body: input });
  return InfraServiceSchema.parse(payload);
}

export async function patchInfraService(id: string, patch: InfraServicePatch): Promise<InfraService> {
  const payload = await apiFetch<unknown>(servicePath(id), { method: 'PATCH', body: patch });
  return InfraServiceSchema.parse(payload);
}

export async function archiveInfraService(id: string): Promise<InfraService> {
  const payload = await apiFetch<unknown>(`${servicePath(id)}/archive`, { method: 'POST' });
  return InfraServiceSchema.parse(payload);
}

/**
 * Records one payment. `idempotencyKey` is minted once per opened «Оплачено»
 * dialog, so a retry after a timeout replays the same payment instead of
 * paying twice.
 */
export async function recordInfraPayment(
  id: string,
  input: RecordInfraPaymentInput,
  idempotencyKey: string,
): Promise<RecordInfraPaymentResult> {
  const payload = await apiFetch<unknown>(`${servicePath(id)}/payments`, {
    method: 'POST',
    body: input,
    headers: { 'Idempotency-Key': idempotencyKey },
  });
  const record = asRecord(payload);
  return {
    payment: InfraPaymentSchema.parse(record.payment),
    service: InfraServiceSchema.parse(record.service),
  };
}

export async function seedInfraDefaults(): Promise<InfraSeedResult> {
  const payload = await apiFetch<unknown>(`${ROOT}/seed-defaults`, { method: 'POST' });
  const record = asRecord(payload);
  return { created: parseCount(record.created, 'created'), existing: parseCount(record.existing, 'existing') };
}

/** Every write changes the registry, the totals and (for a payment) the history. */
export async function invalidateInfraCosts(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: INFRA_SERVICES_QUERY_KEY }),
    queryClient.invalidateQueries({ queryKey: INFRA_SUMMARY_QUERY_KEY }),
    queryClient.invalidateQueries({ queryKey: INFRA_PAYMENTS_QUERY_KEY }),
  ]);
}

export function useInfraServices(includeArchived: boolean) {
  return useQuery({
    queryKey: [...INFRA_SERVICES_QUERY_KEY, includeArchived],
    queryFn: () => fetchInfraServices(includeArchived),
  });
}

export function useInfraSummary() {
  return useQuery({ queryKey: INFRA_SUMMARY_QUERY_KEY, queryFn: fetchInfraSummary });
}

export function useInfraPayments(range: { from: string; to: string }) {
  return useQuery({
    queryKey: [...INFRA_PAYMENTS_QUERY_KEY, range.from, range.to],
    queryFn: () => fetchInfraPayments(range),
  });
}

/** A mutation that refreshes all three reads once it settles successfully. */
function useInfraMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => invalidateInfraCosts(queryClient),
  });
}

export function useCreateInfraService() {
  return useInfraMutation((input: InfraServiceInput) => createInfraService(input));
}

export function usePatchInfraService() {
  return useInfraMutation(({ id, patch }: { id: string; patch: InfraServicePatch }) =>
    patchInfraService(id, patch),
  );
}

export function useArchiveInfraService() {
  return useInfraMutation((id: string) => archiveInfraService(id));
}

export function useRecordInfraPayment() {
  return useInfraMutation(
    ({ id, input, idempotencyKey }: { id: string; input: RecordInfraPaymentInput; idempotencyKey: string }) =>
      recordInfraPayment(id, input, idempotencyKey),
  );
}

export function useSeedInfraDefaults() {
  return useInfraMutation<void, InfraSeedResult>(() => seedInfraDefaults());
}
